import { React } from 'Imports';
import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from 'MaterialUIComponents';
import { IUserManagementServiceInjectedProps, UserManagementService } from '$State/UserManagementFreezerService';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { Pendo } from '../PendoInitializer';
import { apm } from '@elastic/apm-rum';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { useLogin } from './hooks/useLogin';
import { useAppInitialization } from '../contexts/AppInitializationContext';
import SplitIOWrapper from '$Utilities/SplitIOWrapper';
import { getFleetValidationError } from '../router/utils/getFleetValidationError';
import { usePageTitle } from '../router/utils/usePageTitleSetter';

type RequiredAuthProps = IUserManagementServiceInjectedProps & IConfigServiceInjectedProps;

type AuthStatus = 'loading' | 'fleetError' | 'accessError' | 'authenticated';

const _RequiredAuth = (props: RequiredAuthProps) => {
    const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
    const [errorContent, setErrorContent] = useState<React.ReactElement | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isLoggedIn = useLogin(props.userManagement, props.config, navigate, location);
    const { isAppInitialized } = useAppInitialization();
    const setTitle = usePageTitle();

    const handleUserConfig = async () => {
        await props.userManagement.setupUserData();

        apm.addLabels({ UserName: IntegrationPartnerDataService.getUsername() });

        const integrationPlatform = ConfigService.getIntegrationPlatform();

        const fleetValidation = getFleetValidationError();
        if (fleetValidation) {
            setTitle(fleetValidation.title);
            setErrorContent(fleetValidation.element);
            setAuthStatus('fleetError');
            return;
        }

        try {
            await props.userManagement.getCurrentUserInfo();
        } catch (err: any) {
            // Send to APM for diagnostics
            apm.captureError(err);

            if (err?.status !== 503) {
                setErrorContent(<h1 style={{ textAlign: 'center', paddingTop: '20px' }}>You do not have access to this functionality. Please contact your administrator to request access.</h1>);
                setAuthStatus('accessError');
            }

            return;
        }

        const { getCurrentUserInfoResults } = props.userManagement.getState();
        const currentUserInfo = getCurrentUserInfoResults?.data?.data ?? null;

        Pendo.initializePendo();
        Pendo.UpdatePendo(
            integrationPlatform === 'geotab' ? UserManagementService.getUserCredential() : null,
            ConfigService.getDeployEnvironment(),
            integrationPlatform,
            ConfigService.getFleetIdString(),
            currentUserInfo,
        );

        setAuthStatus('authenticated');
    };

    useEffect(() => {
        if (isLoggedIn && isAppInitialized) {
            handleUserConfig();
        }
    }, [isLoggedIn, isAppInitialized]);

    switch (authStatus) {
        case 'fleetError':
        case 'accessError':
            return errorContent;
        case 'authenticated':
            return (
                <SplitIOWrapper>
                    <Outlet />
                </SplitIOWrapper>
            );
        case 'loading':
        default:
            return <CircularProgress />;
    }
};

const RequiredAuth = ConfigService.inject(UserManagementService.inject(_RequiredAuth));

export { RequiredAuth };
